// Shared style tokens/objects for every L10 Meeting section - same palette as the rest of
// the app (indigo/slate, see WinTheDayPage.js), not Bloomgrowth's teal branding.
export const colors = {
  primary: '#6366f1',
  primaryDark: '#4f46e5',
  primarySoft: '#eef2ff',
  green: '#16a34a',
  greenSoft: '#dcfce7',
  red: '#dc2626',
  redSoft: '#fee2e2',
  amber: '#f59e0b',
  amberSoft: '#fef3c7',
  text: '#0f172a',
  subtext: '#475569',
  faint: '#64748b',
  border: '#e5e7eb',
  bgSubtle: '#f8fafc',
};

export const st = {
  page: { padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1.1rem', maxWidth: '1400px', margin: '0 auto', width: '100%' },
  card: {
    background: '#fff', borderRadius: '1rem', padding: '1.25rem 1.4rem',
    boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 16px rgba(15,23,42,0.05)',
    border: `1px solid ${colors.border}`,
  },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '0.9rem', paddingBottom: '0.75rem', borderBottom: `1px solid ${colors.border}`,
  },
  h2: { margin: 0, fontSize: '1.05rem', fontWeight: '800', color: colors.text, letterSpacing: '-0.01em' },
  h3: { margin: 0, fontSize: '0.85rem', fontWeight: '700', color: colors.text },
  eyebrow: { fontSize: '0.65rem', fontWeight: '700', color: colors.primary, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.2rem 0' },
  subtext: { fontSize: '0.78rem', color: colors.faint },
  primaryBtn: {
    padding: '0.5rem 1rem', background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
    color: '#fff', border: 'none', borderRadius: '0.6rem', cursor: 'pointer', fontSize: '0.8rem',
    fontWeight: '700', boxShadow: '0 1px 2px rgba(79,70,229,0.3), 0 2px 8px rgba(79,70,229,0.18)',
    transition: 'transform 0.1s ease',
  },
  secondaryBtn: {
    padding: '0.5rem 0.9rem', background: '#fff', border: `1.5px solid ${colors.border}`,
    borderRadius: '0.6rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '700', color: colors.subtext,
  },
  ghostBtn: {
    padding: '0.3rem 0.5rem', background: 'transparent', border: 'none',
    borderRadius: '0.4rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', color: colors.faint,
  },
  input: {
    padding: '0.5rem 0.7rem', border: `1.5px solid ${colors.border}`, borderRadius: '0.6rem',
    fontSize: '0.8rem', outline: 'none', color: colors.text, background: '#fff',
  },
  textarea: {
    padding: '0.6rem 0.75rem', border: `1.5px solid ${colors.border}`, borderRadius: '0.6rem',
    fontSize: '0.8rem', outline: 'none', color: colors.text, resize: 'vertical', minHeight: '90px',
    fontFamily: 'inherit', lineHeight: 1.5,
  },
  tabStrip: {
    display: 'flex', gap: '0.3rem', background: '#fff', borderRadius: '0.85rem',
    padding: '0.4rem', boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 16px rgba(15,23,42,0.05)',
    border: `1px solid ${colors.border}`, flexWrap: 'wrap',
  },
  tab: (active) => ({
    padding: '0.55rem 1rem', borderRadius: '0.55rem', border: 'none', cursor: 'pointer',
    fontSize: '0.78rem', fontWeight: '700', transition: 'all 0.12s ease',
    background: active ? `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})` : 'transparent',
    color: active ? '#fff' : colors.subtext,
    boxShadow: active ? '0 2px 8px rgba(79,70,229,0.28)' : 'none',
  }),
  statusBadge: (status) => {
    const map = {
      on_track: { bg: colors.primarySoft, color: colors.primaryDark, label: 'On track' },
      off_track: { bg: colors.redSoft, color: colors.red, label: 'Off track' },
      done: { bg: colors.greenSoft, color: colors.green, label: 'Done' },
    };
    const s = map[status] || map.on_track;
    return {
      style: {
        background: s.bg, color: s.color, padding: '0.3rem 0.75rem', borderRadius: '999px',
        fontSize: '0.68rem', fontWeight: '800', border: 'none', cursor: 'pointer', letterSpacing: '0.01em',
      },
      label: s.label,
    };
  },
  pill: {
    display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: colors.bgSubtle,
    borderRadius: '999px', padding: '0.3rem 0.7rem', fontSize: '0.72rem', fontWeight: '600', color: colors.subtext,
  },
  table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.75rem' },
  th: {
    padding: '0.5rem 0.5rem', background: colors.bgSubtle, color: colors.faint, fontWeight: '700',
    fontSize: '0.63rem', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right',
    borderBottom: `2px solid ${colors.border}`, whiteSpace: 'nowrap',
  },
  td: {
    padding: '0.5rem 0.5rem', borderBottom: `1px solid #f1f5f9`, color: colors.text,
    verticalAlign: 'middle', textAlign: 'right', fontSize: '0.72rem',
  },
  emptyState: {
    padding: '2.5rem 1rem', textAlign: 'center', color: colors.faint, fontSize: '0.85rem',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem',
  },
  rowHover: {
    borderRadius: '0.6rem', transition: 'background 0.1s ease',
  },
};
